// Reference Library Return-Workflow — shared navigation context helper.
//
// Lets an originating engineering editor (BHA, Drill String, Well Profile,
// …) send the operator into a Reference Library to add/edit a record and
// come straight back to the exact same editor with in-progress data intact.
//
// Carried entirely via React Router's `location.state` (native History API
// state — survives a same-tab refresh; only lost on a genuinely new
// browsing context such as opening the link in a new tab). No new storage
// key, no schema change — this is presentation/navigation plumbing only.
//
// Protocol:
//   1. The originating editor's "Manage ___ Library" link/navigation sets
//      state: { rsLibReturn: { from, draft, anchorId } } via
//      `libraryReturnState`. `anchorId` (optional) is the DOM id of the
//      component/section/card the operator was working in when they left —
//      e.g. `bha-component-3` — used to restore their place, not just data.
//   2. Every navigation inside the Reference Library screens (Hub, Record
//      Editor) forwards the SAME rsLibReturn untouched via
//      `forwardLibraryReturn` — so it survives Hub → New/Edit Record hops.
//   3. The ← BACK action reads rsLibReturn (`getLibraryReturnCtx`) and
//      navigates to `from`, handing the draft + anchor back via
//      state: { rsLibRestoreDraft, rsLibReturnAnchor } (`restoreDraftState`).
//   4. The originating editor consumes rsLibRestoreDraft once on mount
//      (`getRestoredDraft`), then — after ScrollToTop's own same-URL guard
//      lets a same-path history replace pass through without resetting
//      scroll (see components/ScrollToTop.jsx) — brings `rsLibReturnAnchor`
//      (`getRestoredAnchor`) into view. Both are cleared from history state
//      immediately so a later refresh never re-applies them.

export function libraryReturnState(location, draft, anchorId) {
  return {
    rsLibReturn: {
      from: location.pathname + location.search + location.hash,
      draft,
      anchorId: anchorId || null,
    },
  };
}

export function getLibraryReturnCtx(location) {
  return location?.state?.rsLibReturn || null;
}

export function forwardLibraryReturn(location) {
  const ctx = getLibraryReturnCtx(location);
  return ctx ? { rsLibReturn: ctx } : undefined;
}

export function restoreDraftState(ctx) {
  return { rsLibRestoreDraft: ctx.draft, rsLibReturnAnchor: ctx.anchorId || null };
}

export function getRestoredDraft(location) {
  return location?.state?.rsLibRestoreDraft ?? null;
}

export function getRestoredAnchor(location) {
  return location?.state?.rsLibReturnAnchor ?? null;
}

// Brings a restored anchor element into view once the DOM has settled
// (double rAF — same technique already used by every "scroll to section"
// call elsewhere in these editors). Soft-fails silently if the element
// isn't found (e.g. the row was reordered/removed) — position restoration
// is a best-effort convenience, never a blocker.
export function scrollToRestoredAnchor(anchorId) {
  if (!anchorId) return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const el = document.getElementById(anchorId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    })
  );
}
